import { useState, useEffect, useCallback } from "react";
import apiFetch from "../../../shared/services/apiClient";

const BASE = process.env.REACT_APP_API_URL ?? "";

// ── Condominio ───────────────────────────────────────────────────────────

export function useCondominio() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // IMPORTANTE: no llamar "fetch" — sobreescribe window.fetch y rompe update()
  const loadCondominio = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/api/residents/condominio/");
      const json = await res.json();
      setData(json.results?.[0] ?? json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCondominio();
  }, [loadCondominio]);

  async function update(formData) {
    if (!data) return;
    // Usar window.fetch directamente para multipart (apiFetch siempre pone Content-Type: application/json)
    const res = await window.fetch(
      `${BASE}/api/residents/condominio/${data.id}/`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: formData, // FormData — el browser pone el Content-Type con boundary automáticamente
      },
    );
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || "Error al guardar");
    }
    const updated = await res.json();
    setData(updated);
    return updated;
  }

  async function clearImage(field) {
    if (!data) return;
    // Usa el endpoint dedicado /clear-image/ que elimina el archivo del disco
    const res = await window.fetch(
      `${BASE}/api/residents/condominio/${data.id}/clear-image/`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ field }),
      }
    );
    if (!res.ok) throw new Error('Error al eliminar imagen');
    // Recargar el condominio para obtener las URLs actualizadas
    await loadCondominio();
  }

  return { data, loading, error, refetch: loadCondominio, update, clearImage };
}

// ── Rubros ───────────────────────────────────────────────────────────────

export function useRubros() {
  const [rubros, setRubros] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRubros = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/api/finance/rubro/");
      const json = await res.json();
      setRubros(json.results ?? json);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRubros();
  }, [loadRubros]);

  async function create(nombre) {
    const res = await apiFetch("/api/finance/rubro/", {
      method: "POST",
      body: JSON.stringify({ nombre }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(
        err?.nombre?.[0] ?? err?.detail ?? "Error al crear rubro",
      );
    }
    await loadRubros();
  }

  async function remove(id) {
    const res = await apiFetch(`/api/finance/rubro/${id}/`, {
      method: "DELETE",
    });
    if (!res.ok)
      throw new Error("No se puede eliminar (tiene facturas asociadas)");
    await loadRubros();
  }

  async function update(id, nombre) {
    const res = await apiFetch(`/api/finance/rubro/${id}/`, {
      method: "PATCH",
      body: JSON.stringify({ nombre }),
    });
    if (!res.ok) throw new Error("Error al actualizar rubro");
    await loadRubros();
  }

  return { rubros, loading, create, remove, update, refetch: loadRubros };
}

// ── Cuentas Bancarias ────────────────────────────────────────────────────

export function useCuentas() {
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadCuentas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/api/finance/cuentaBancaria/");
      const json = await res.json();
      setCuentas(json.results ?? json);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCuentas();
  }, [loadCuentas]);

  async function create(cuenta) {
    const res = await apiFetch("/api/finance/cuentaBancaria/", {
      method: "POST",
      body: JSON.stringify(cuenta),
    });
    if (!res.ok) throw new Error("Error al crear cuenta");
    await loadCuentas();
  }

  async function remove(id) {
    const res = await apiFetch(`/api/finance/cuentaBancaria/${id}/`, {
      method: "DELETE",
    });
    if (!res.ok) throw new Error("No se puede eliminar");
    await loadCuentas();
  }

  async function update(id, cuenta) {
    const res = await apiFetch(`/api/finance/cuentaBancaria/${id}/`, {
      method: "PATCH",
      body: JSON.stringify(cuenta),
    });
    if (!res.ok) throw new Error("Error al actualizar cuenta");
    await loadCuentas();
  }

  async function sincronizarCatalogo() {
    await apiFetch("/api/finance/cuentaBancaria/sincronizar-catalogo/", { method:"POST" });
    await loadCuentas();
  }
  return { cuentas, loading, create, remove, update, refetch: loadCuentas, sincronizarCatalogo };
}
